import { makeAutoObservable } from 'mobx';
import logger from '../helpers/logger';
import { Customer } from '../typegen/customer';
import { Item } from '../typegen/item';
import { PaymentType } from '../typegen/payment-type';

export interface ICartItem {
	id: string;
	customer?: Customer;
	item: Item;
	isAmountConfirmed: boolean;
	amountToPay: number;
	taxYear?: string;
	isInactiveAccount?: boolean;
}

export class CartItem implements ICartItem {
	id: string;
	customer?: Customer;
	item: Item;
	isAmountConfirmed: boolean;
	amountToPay: number;
	cashAmountPaid?: number;
	taxYear?: string;
	isInactiveAccount?: boolean;

	constructor(item: ICartItem) {
		this.id = item.id;
		this.customer = item.customer;
		this.item = item.item;
		this.isAmountConfirmed = item.isAmountConfirmed;
		this.amountToPay = item.amountToPay;
		this.taxYear = item.taxYear;
		this.isInactiveAccount = item.isInactiveAccount;
		makeAutoObservable(this);
	}

	get amountPaid(): number {
		return this.cashAmountPaid ?? this.amountToPay;
	}

	get feeAmount(): number {
		try {
			const paymentFee = this.item.paymentMethods.find((p) => (p.paymentType & PaymentType.Card) !== 0)?.paymentFee;
			if (paymentFee?.feeAmount === undefined) throw new Error('Payment method fee amount not found on cart item');
			if (paymentFee.isFeeAmountFlat) return paymentFee.feeAmount;
			return Math.round(this.amountToPay * paymentFee.feeAmount * 100) / 100;
		} catch {
			logger.debug.error(
				'Error getting fee amount for cart item. This error should be revisited later to get rid of it',
				`CartItem ID: ${this.id}`
			);
			return 0;
		}
	}

	get checkFeeAmount(): number {
		try {
			const paymentFee = this.item.paymentMethods.find((p) => (p.paymentType & PaymentType.Check) !== 0)?.paymentFee;
			if (paymentFee?.feeAmount === undefined) throw new Error('Check payment method fee amount not found on cart item');
			if (paymentFee.isFeeAmountFlat) return paymentFee.feeAmount;
			return Math.round(this.amountToPay * paymentFee.feeAmount * 100) / 100;
		} catch {
			logger.debug.error(
				'Error getting check fee amount for cart item. This error should be revisited later to get rid of it',
				`CartItem ID: ${this.id}`
			);
			return 0;
		}
	}

	get approximateAccountBalance(): number {
		return this.item.amountDue - this.amountPaid;
	}

	get amountToPayWithFee(): number {
		if (this.cashAmountPaid !== undefined) return this.cashAmountPaid + this.feeAmount;
		return this.amountToPay + this.feeAmount;
	}

	get amountToPayWithCheckFee(): number {
		if (this.cashAmountPaid !== undefined) return this.cashAmountPaid + this.checkFeeAmount;
		return this.amountToPay + this.checkFeeAmount;
	}
}
